import { formatarData } from './catalogo.js';
import type { Avaliacao, Visita } from './fonte.js';

/**
 * Consulta de fichas (pagina Dados) como dado: filtros lidos da URL, recorte,
 * ordem e descricao em linguagem simples. Os filtros moram na URL para as
 * visoes rapidas ("Fichas por validar") serem so um link.
 */

export type SituacaoFicha = 'abertas' | 'fechadas' | 'validadas';
export type NivelFicha = '1' | '2';
/** Ultimos N dias ate hoje, ou "pers" (De/Ate escolhidos a mao). */
export type Periodo = '30' | '90' | '365' | 'pers';
export type OrdemFichas = 'data' | 'animal' | 'medidas';

export interface FiltrosConsulta {
  visita: string;
  nivel: NivelFicha | '';
  situacao: SituacaoFicha | '';
  periodo: Periodo | '';
  de: string;
  ate: string;
}

export const SITUACOES: SituacaoFicha[] = ['abertas', 'fechadas', 'validadas'];
export const PERIODOS: Periodo[] = ['30', '90', '365', 'pers'];
export const ROTULO_SITUACAO_FICHA: Record<SituacaoFicha, string> = {
  abertas: 'Em aberto',
  fechadas: 'Fechadas, sem validação',
  validadas: 'Validadas',
};
export const ROTULO_PERIODO: Record<Periodo, string> = {
  '30': '30 dias',
  '90': '90 dias',
  '365': '12 meses',
  pers: 'Personalizado',
};
export const ROTULO_NIVEL: Record<NivelFicha, string> = { '1': 'Censo', '2': 'Completa' };

const um = <T extends string>(lista: readonly T[], v: string | null): T | '' => lista.find((x) => x === v) ?? '';
const ehData = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '');

export function lerFiltrosConsulta(p: URLSearchParams): FiltrosConsulta {
  const periodo = um(PERIODOS, p.get('periodo'));
  return {
    visita: p.get('visita') ?? '',
    nivel: um(['1', '2'] as const, p.get('nivel')),
    situacao: um(SITUACOES, p.get('situacao')),
    periodo,
    // De/Ate so valem no periodo personalizado: evita filtro escondido na URL.
    de: periodo === 'pers' ? ehData(p.get('de')) : '',
    ate: periodo === 'pers' ? ehData(p.get('ate')) : '',
  };
}

export function filtrosAtivosConsulta(f: FiltrosConsulta): number {
  return [f.visita, f.nivel, f.situacao, f.periodo].filter(Boolean).length;
}

const fechada = (a: Avaliacao) => a.status === 'fechada' || a.status === 'validada';

export function situacaoDaFicha(a: Avaliacao): SituacaoFicha {
  if (a.status === 'validada') return 'validadas';
  if (a.status === 'fechada') return 'fechadas';
  return 'abertas';
}

/** Primeiro dia do periodo (inclusive), ou null sem limite. */
export function inicioDoPeriodo(f: FiltrosConsulta, hoje: string): string | null {
  if (f.periodo === 'pers') return f.de || null;
  if (!f.periodo) return null;
  const d = new Date(`${hoje}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - Number(f.periodo) + 1);
  return d.toISOString().slice(0, 10);
}

export function filtrarFichas(
  fichas: readonly Avaliacao[],
  f: FiltrosConsulta,
  hoje: string,
  ignorar: ReadonlyArray<keyof FiltrosConsulta> = [],
): Avaliacao[] {
  const vale = (k: keyof FiltrosConsulta) => !ignorar.includes(k);
  const desde = vale('periodo') ? inicioDoPeriodo(f, hoje) : null;
  const ate = vale('periodo') && f.periodo === 'pers' ? f.ate || null : null;
  return fichas.filter(
    (a) =>
      (!vale('visita') || !f.visita || a.visit_id === f.visita) &&
      (!vale('nivel') || !f.nivel || (f.nivel === '2' ? a.protocol_level >= 2 : a.protocol_level === 1)) &&
      (!vale('situacao') || !f.situacao || situacaoDaFicha(a) === f.situacao) &&
      (!desde || a.visit_date >= desde) &&
      (!ate || a.visit_date <= ate),
  );
}

/** Ordem estavel: desempate pela data mais nova e depois pelo id. */
export function ordenarFichas(
  fichas: readonly Avaliacao[],
  ordem: OrdemFichas,
  dir: 'asc' | 'desc',
  nome: ReadonlyMap<string, string>,
  medidas: ReadonlyMap<string, number>,
): Avaliacao[] {
  const s = dir === 'asc' ? 1 : -1;
  const desempate = (a: Avaliacao, b: Avaliacao) => b.visit_date.localeCompare(a.visit_date) || a.id.localeCompare(b.id);
  return [...fichas].sort((a, b) => {
    if (ordem === 'animal')
      return s * (nome.get(a.animal_id) ?? '').localeCompare(nome.get(b.animal_id) ?? '', 'pt-BR') || desempate(a, b);
    if (ordem === 'medidas') return s * ((medidas.get(a.id) ?? 0) - (medidas.get(b.id) ?? 0)) || desempate(a, b);
    return s * a.visit_date.localeCompare(b.visit_date) || a.id.localeCompare(b.id);
  });
}

export function contarSituacoes(fichas: readonly Avaliacao[]): Record<SituacaoFicha, number> {
  const r: Record<SituacaoFicha, number> = { abertas: 0, fechadas: 0, validadas: 0 };
  for (const a of fichas) r[situacaoDaFicha(a)]++;
  return r;
}

export function descreverConsulta(f: FiltrosConsulta, visitas: readonly Visita[], hoje: string): string[] {
  const p: string[] = [];
  if (f.situacao) p.push(`situação: ${ROTULO_SITUACAO_FICHA[f.situacao].toLocaleLowerCase('pt-BR')}`);
  if (f.nivel) p.push(`só fichas de ${ROTULO_NIVEL[f.nivel].toLocaleLowerCase('pt-BR')}`);
  if (f.visita) {
    const v = visitas.find((x) => x.id === f.visita);
    p.push(v ? `visita nº ${v.visit_number} (${formatarData(v.visit_date)})` : 'uma visita');
  }
  if (f.periodo === 'pers') {
    if (f.de || f.ate)
      p.push(`de ${f.de ? formatarData(f.de) : 'o início'} até ${f.ate ? formatarData(f.ate) : formatarData(hoje)}`);
  } else if (f.periodo) {
    p.push(`últimos ${ROTULO_PERIODO[f.periodo]}, de ${formatarData(inicioDoPeriodo(f, hoje)!)} a ${formatarData(hoje)}`);
  }
  return p;
}

export { fechada as fichaFechada };
